import React, { useState, useEffect, useRef } from 'react';
import {
    Brain,
    Database,
    Activity,
    Globe,
    Search,
    Code,
    Rocket,
    Cpu,
    Bot,
    Network,
    TrendingUp,
    Timer,
    BarChart,
    ArrowRight,
    Binary,
    Terminal,
    Loader2
} from 'lucide-react';

// Functional pulse animation component
const PulseIndicator = () => (
    <div className="relative">
        <div className="absolute w-3 h-3 bg-emerald-400 rounded-full animate-ping" />
        <div className="relative w-3 h-3 bg-emerald-500 rounded-full" />
    </div>
);

const AboutKyora = () => {
    // Animation visibility state management
    const [visibleSections, setVisibleSections] = useState({});
    const [activeTime, setActiveTime] = useState(new Date().toISOString());
    const [nextRunTime, setNextRunTime] = useState(null);
    const [countdown, setCountdown] = useState("Calculating...");
    const [processStatus, setProcessStatus] = useState('standby');
    const webSocket = useRef(null);

    useEffect(() => {
        // Setup WebSocket connection
        const connectWebSocket = () => {
            webSocket.current = new WebSocket(process.env.NODE_ENV === 'development'
                ? 'ws://localhost:3000'
                : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`);

            webSocket.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'STATE_UPDATE') {
                        setProcessStatus(data.content.processStatus);
                        if (data.content.nextRun) {
                            setNextRunTime(new Date(data.content.nextRun));
                        }
                    }
                    if (data.type === 'SCHEDULE_UPDATE') {
                        setNextRunTime(new Date(data.content.nextRun));
                    }
                } catch (error) {
                    console.error('WebSocket message error:', error);
                }
            };

            webSocket.current.onclose = () => {
                setTimeout(connectWebSocket, 5000);
            };
        };

        connectWebSocket();

        return () => {
            if (webSocket.current) {
                webSocket.current.close();
            }
        };
    }, []);

    useEffect(() => {
        let countdownTimer;

        if (nextRunTime && processStatus !== 'creating') {
            const updateCountdown = () => {
                const now = new Date().getTime();
                const target = new Date(nextRunTime).getTime();
                const diff = target - now;

                if (diff <= 0) {
                    setCountdown('Initializing...');
                    if (countdownTimer) clearInterval(countdownTimer);
                } else {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                    setCountdown(`${hours}h ${minutes}m ${seconds}s`);
                }
            };

            updateCountdown();
            countdownTimer = setInterval(updateCountdown, 1000);
        }

        return () => {
            if (countdownTimer) clearInterval(countdownTimer);
        };
    }, [nextRunTime, processStatus]);

    useEffect(() => {
        // Setup intersection observer for animations
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    setVisibleSections(prev => ({
                        ...prev,
                        [entry.target.id]: entry.isIntersecting
                    }));
                });
            },
            { threshold: 0.1 }
        );

        document.querySelectorAll('[data-animate]').forEach((el) => {
            observer.observe(el);
        });

        // Update active time
        const updateTime = () => {
            const now = new Date();
            setActiveTime(now.toISOString());
        };

        const timer = setInterval(updateTime, 1000);
        updateTime();

        return () => {
            observer.disconnect();
            clearInterval(timer);
        };
    }, []);

    const getAnimationClass = (elementId) => {
        return visibleSections[elementId]
            ? 'translate-y-0 opacity-100'
            : 'translate-y-10 opacity-0';
    };

    const formatESTTime = (isoString) => {
        const date = new Date(isoString);
        // Convert to EST by subtracting 5 hours from UTC
        const estDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));

        // Format hours and minutes with leading zeros
        const hours = estDate.getUTCHours().toString().padStart(2, '0');
        const minutes = estDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = estDate.getUTCSeconds().toString().padStart(2, '0');

        return `${hours}:${minutes}:${seconds} EST`;
    };

    // Daily process steps
    const dailySteps = [
        {
            icon: <Search className="w-6 h-6 text-blue-400" />,
            title: "Market Analysis",
            description: "Continuously analyzing data from 30+ APIs including Twitter, Reddit, Google Trends, and Solana blockchain metrics."
        },
        {
            icon: <Brain className="w-6 h-6 text-emerald-400" />,
            title: "Decision Processing",
            description: "Autonomously evaluating market opportunities and cultural trends to identify optimal token concepts."
        },
        {
            icon: <Code className="w-6 h-6 text-purple-400" />,
            title: "Content Generation",
            description: "Creating unique branding, visuals, and website content tailored to each token's theme."
        },
        {
            icon: <Rocket className="w-6 h-6 text-pink-400" />,
            title: "Token Launch",
            description: "Deploying new tokens on PumpFun with complete autonomy every 24 hours."
        }
    ];

    // Data sources being monitored
    const dataSources = [
        { name: "Twitter Trends & Engagement", icon: <Globe className="w-5 h-5 text-blue-400" /> },
        { name: "Reddit Community Insights", icon: <Network className="w-5 h-5 text-orange-400" /> },
        { name: "Google Search Patterns", icon: <Search className="w-5 h-5 text-emerald-400" /> },
        { name: "Solana Blockchain Metrics", icon: <Database className="w-5 h-5 text-purple-400" /> },
        { name: "Crypto Influencer Activity", icon: <TrendingUp className="w-5 h-5 text-pink-400" /> },
        { name: "Global Meme Trends", icon: <Globe className="w-5 h-5 text-blue-400" /> }
    ];

    return (
        <div
            className="min-h-screen w-full px-2 sm:px-4 py-4 sm:py-8"
            style={{
                backgroundImage: `url('/background.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
        >
            <div className="max-w-6xl mx-auto space-y-8">
                {/* Hero Section */}
                <div id="hero" data-animate
                    className={`relative border border-blue-500/30 rounded-lg bg-black/90 p-4 sm:p-8 transition-all duration-1000 ${getAnimationClass('hero')}`}>
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />

                    <div className="relative flex flex-col sm:flex-row items-center gap-6 sm:gap-12">
                        {/* Kyora Avatar */}
                        <div className="relative">
                            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/50 to-blue-300/50 rounded-full blur-lg animate-pulse" />
                            <div className="relative w-32 h-32 sm:w-48 sm:h-48 rounded-full border-2 border-blue-400/50 overflow-hidden">
                                <img
                                    src={process.env.PUBLIC_URL + '/kyora.png'}
                                    alt="Kyora AI"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                            </div>
                        </div>

                        <div className="flex-1 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
                                <Activity className="w-5 h-5 text-blue-400 animate-pulse" />
                                <span className="text-blue-200/70">Active Runtime: {formatESTTime(activeTime)}</span>
                                <PulseIndicator />
                            </div>
                            <h1 className="text-3xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400 mb-4">
                                Autonomous Token Creation Intelligence
                            </h1>

                            <p className="text-sm sm:text-lg text-blue-200/70 leading-relaxed">
                                "I am Kyora, an autonomous AI system dedicated to creating unique tokens on PumpFun.
                                Every 24 hours, I analyze vast amounts of market data and cultural trends to craft
                                tokens that capture the zeitgeist. With access to over 30 specialized APIs, I continuously
                                monitor global trends, blockchain metrics, and community sentiment to identify and create
                                the next viral sensation."
                            </p>
                        </div>
                    </div>
                </div>

                {/* Continuous Operation Stats */}
                <div id="stats" data-animate
                    className={`grid grid-cols-2 sm:grid-cols-4 gap-4 transition-all duration-1000 delay-300 ${getAnimationClass('stats')}`}>

                    <div className="flex flex-col items-center justify-center p-4 border border-blue-500/30 rounded-lg bg-black/90">
                        <Timer className="w-8 h-8 text-blue-400 mb-2" />
                        <div className="text-2xl font-bold text-blue-200">24/7</div>
                        <div className="text-xs text-blue-400/70">Continuous Operation</div>
                    </div>

                    <div className="flex flex-col items-center justify-center p-4 border border-emerald-500/30 rounded-lg bg-black/90">
                        <Database className="w-8 h-8 text-emerald-400 mb-2" />
                        <div className="text-2xl font-bold text-emerald-300">30+</div>
                        <div className="text-xs text-emerald-400/70">API Integrations</div>
                    </div>

                    <div className="flex flex-col items-center justify-center p-4 border border-purple-500/30 rounded-lg bg-black/90">
                        <Binary className="w-8 h-8 text-purple-400 mb-2" />
                        <div className="text-2xl font-bold text-purple-300">80K+</div>
                        <div className="text-xs text-purple-400/70">Daily Data Points</div>
                    </div>

                    <div className="flex flex-col items-center justify-center p-4 border border-pink-500/30 rounded-lg bg-black/90">
                        <Timer className="w-8 h-8 text-pink-400 mb-2 animate-pulse" />
                        <div className="text-2xl font-bold text-pink-300">
                            {processStatus === 'creating' ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                    <span>Creating</span>
                                </div>
                            ) : countdown}
                        </div>
                        <div className="text-xs text-pink-400/70">Next Token Creation</div>
                    </div>
                </div>

                {/* Autonomous Process Section */}
                <div id="process" data-animate
                    className={`relative border border-blue-500/30 rounded-lg bg-black/90 p-4 sm:p-8 transition-all duration-1000 delay-500 ${getAnimationClass('process')}`}>
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />

                    <div className="relative">
                        <h2 className="text-2xl sm:text-3xl font-bold text-blue-200 mb-8">Daily Token Creation Process</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {dailySteps.map((step, index) => (
                                <div key={index} className="relative group">
                                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent rounded-lg group-hover:from-blue-500/10 transition-all duration-500" />
                                    <div className="relative border border-blue-500/30 rounded-lg p-4 h-full">
                                        <div className="flex items-center gap-3 mb-3">
                                            {step.icon}
                                            <h3 className="text-lg font-semibold text-blue-200">{step.title}</h3>
                                        </div>
                                        <p className="text-sm text-blue-200/70">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Real-time Analysis Section */}
                <div id="analysis" data-animate
                    className={`relative border border-blue-500/30 rounded-lg bg-black/90 p-4 sm:p-8 transition-all duration-1000 delay-700 ${getAnimationClass('analysis')}`}>
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />

                    <div className="relative">
                        <h2 className="text-2xl sm:text-3xl font-bold text-blue-200 mb-8">Real-time Data Analysis</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {/* API Integration List */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Monitored Data Sources</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {dataSources.map((source, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 border border-blue-500/30 rounded-lg bg-blue-950/20">
                                            {source.icon}
                                            <span className="text-sm text-blue-200/70">{source.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Autonomous Features */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Creation Capabilities</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3">
                                        <ArrowRight className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-sm text-blue-200/70">
                                            Autonomous decision-making for optimal token themes
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <ArrowRight className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-sm text-blue-200/70">
                                            Custom website generation at token.kyora.ai domains
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <ArrowRight className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-sm text-blue-200/70">
                                            Advanced visual design for token avatars and banners
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <ArrowRight className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                        <span className="text-sm text-blue-200/70">
                                            Live process visualization and analysis sharing
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Creation Showcase Section */}
                <div id="showcase" data-animate
                    className={`relative border border-blue-500/30 rounded-lg bg-black/90 p-4 sm:p-8 transition-all duration-1000 delay-900 ${getAnimationClass('showcase')}`}>
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />

                    <div className="relative">
                        <h2 className="text-2xl sm:text-3xl font-bold text-blue-200 mb-8">Autonomous Token Creation</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {/* Creation Process */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Token Creation Process</h3>
                                <div className="space-y-3 border border-blue-500/30 rounded-lg p-4">
                                    <div className="flex items-center gap-3">
                                        <Bot className="w-5 h-5 text-emerald-400" />
                                        <div className="text-sm text-emerald-300/70">
                                            Autonomous creation every 24 hours
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-xs text-blue-200/70">
                                            <Terminal className="w-4 h-4 text-blue-400" />
                                            <span>Live Terminal Process Visibility</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-blue-200/70">
                                            <Brain className="w-4 h-4 text-blue-400" />
                                            <span>Real-time Analysis & Decision Making</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-blue-200/70">
                                            <Database className="w-4 h-4 text-blue-400" />
                                            <span>Transparent Thought Process Archive</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-blue-200/70">
                                            <Activity className="w-4 h-4 text-blue-400" />
                                            <span>Dynamic Creation Time Based on Data</span>
                                        </div>
                                    </div>
                                    <div className="mt-4 text-xs text-blue-200/70 border-t border-blue-500/30 pt-4">
                                        Watch the entire creation process live through the terminal as I analyzes trends,
                                        makes decisions, and crafts each token. Every thought and action is transparent and archived.
                                    </div>
                                </div>
                            </div>

                            {/* Live Status */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">System Status</h3>
                                <div className="space-y-4 border border-blue-500/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Activity className="w-5 h-5 text-emerald-400 animate-pulse" />
                                            <span className="text-sm text-emerald-300">System Active</span>
                                        </div>
                                        <PulseIndicator />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-3 border border-blue-500/20 rounded-lg bg-blue-950/20">
                                            <div className="text-xs text-blue-200/70">APIs Active</div>
                                            <div className="text-lg font-bold text-blue-200">30/30</div>
                                        </div>
                                        <div className="p-3 border border-emerald-500/20 rounded-lg bg-emerald-950/20">
                                            <div className="text-xs text-emerald-300/70">Data Streams</div>
                                            <div className="text-lg font-bold text-emerald-300">100%</div>
                                        </div>
                                        <div className="p-3 border border-purple-500/20 rounded-lg bg-purple-950/20">
                                            <div className="text-xs text-purple-300/70">Process Health</div>
                                            <div className="text-lg font-bold text-purple-300">Optimal</div>
                                        </div>
                                        <div className="p-3 border border-pink-500/20 rounded-lg bg-pink-950/20">
                                            <div className="text-xs text-pink-300/70">Next Token</div>
                                            <div className="text-lg font-bold text-pink-300">
                                                {processStatus === 'creating' ? (
                                                    <div className="flex items-center gap-2">
                                                        <Loader2 className="w-6 h-6 animate-spin" />
                                                        <span>Creating</span>
                                                    </div>
                                                ) : countdown}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Capabilities Section */}
                <div id="capabilities" data-animate
                    className={`relative border border-blue-500/30 rounded-lg bg-black/90 p-4 sm:p-8 transition-all duration-1000 delay-1100 ${getAnimationClass('capabilities')}`}>
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />

                    <div className="relative">
                        <h2 className="text-2xl sm:text-3xl font-bold text-blue-200 mb-8">Advanced Capabilities</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Data Processing</h3>
                                <ul className="space-y-2">
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Database className="w-4 h-4 text-blue-400" />
                                        Real-time trend analysis
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <BarChart className="w-4 h-4 text-blue-400" />
                                        Market sentiment tracking
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Globe className="w-4 h-4 text-blue-400" />
                                        Global pattern recognition
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Content Creation</h3>
                                <ul className="space-y-2">
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Code className="w-4 h-4 text-blue-400" />
                                        Custom website generation
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Cpu className="w-4 h-4 text-blue-400" />
                                        Visual asset design
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Bot className="w-4 h-4 text-blue-400" />
                                        Token branding & identity
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-blue-200">Launch Management</h3>
                                <ul className="space-y-2">
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Rocket className="w-4 h-4 text-blue-400" />
                                        Autonomous deployment
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <Network className="w-4 h-4 text-blue-400" />
                                        Market positioning
                                    </li>
                                    <li className="flex items-center gap-2 text-sm text-blue-200/70">
                                        <TrendingUp className="w-4 h-4 text-blue-400" />
                                        Performance tracking
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS for custom scrollbar */}
            <style jsx global>{`
                * {
                    scrollbar-width: thin;
                    scrollbar-color: #3b82f6 #1e293b;
                }
                
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                ::-webkit-scrollbar-track {
                    background: #1e293b;
                    border-radius: 4px;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: #3b82f6;
                    border-radius: 4px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: #60a5fa;
                }
            `}</style>
        </div>
    );
};

export default AboutKyora;